// File: webelieve-frontend/src/features/notes/components/NoteCollaborationSettingsDrawer.tsx
// Right-side settings drawer for note collaboration management

import React, { useEffect, useMemo, useState } from 'react';
import {
  useNoteCollaborationStore,
  type NoteCollaborationSettings,
} from '../stores/useNoteCollaborationStore';

type DrawerTab = 'collaborators' | 'invitations' | 'activity' | 'settings';

interface NoteCollaborationSettingsDrawerProps {
  isOpen: boolean;
  noteId: string;
  noteTitle?: string;
  onClose: () => void;
  onInviteClick: () => void;
}

const DEFAULT_SETTINGS: NoteCollaborationSettings = {
  id: 'default',
  note_id: '',
  comments_enabled: true,
  track_changes_enabled: true,
  suggestions_mode: false,
  auto_save_interval: 30,
  max_concurrent_editors: 10,
  collaboration_mode: 'collaborative',
  notification_settings: null,
  updated_at: new Date().toISOString(),
};

function formatRelativeDate(value: string): string {
  const date = new Date(value);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function humanizeActivityType(value: string): string {
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const NoteCollaborationSettingsDrawer: React.FC<NoteCollaborationSettingsDrawerProps> = ({
  isOpen,
  noteId,
  noteTitle,
  onClose,
  onInviteClick,
}) => {
  const {
    getCollaborators,
    getInvitations,
    getActivity,
    getSettings,
    isLoadingCollaborators,
    isLoadingInvitations,
    isLoadingActivity,
    isLoadingSettings,
    fetchCollaborators,
    fetchInvitations,
    fetchActivity,
    fetchSettings,
    updateCollaboratorRole,
    removeCollaborator,
    revokeInvitation,
    updateSettings,
    error,
    clearError,
  } = useNoteCollaborationStore();

  const collaborators = getCollaborators(noteId);
  const invitations = getInvitations(noteId);
  const activity = getActivity(noteId);
  const persistedSettings = getSettings(noteId);

  const loadingCollaborators = isLoadingCollaborators(noteId);
  const loadingInvitations = isLoadingInvitations(noteId);
  const loadingActivity = isLoadingActivity(noteId);
  const loadingSettings = isLoadingSettings(noteId);

  const [activeTab, setActiveTab] = useState<DrawerTab>('collaborators');
  const [savingSettings, setSavingSettings] = useState(false);
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [processingInvitationId, setProcessingInvitationId] = useState<string | null>(null);
  const [localSettings, setLocalSettings] = useState<NoteCollaborationSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    if (!isOpen || !noteId) return;

    clearError();
    fetchCollaborators(noteId);
    fetchInvitations(noteId);
    fetchActivity(noteId);
    fetchSettings(noteId);
  }, [
    isOpen,
    noteId,
    clearError,
    fetchCollaborators,
    fetchInvitations,
    fetchActivity,
    fetchSettings,
  ]);

  useEffect(() => {
    if (!persistedSettings) return;
    setLocalSettings(persistedSettings);
  }, [persistedSettings]);

  const activeInvitations = useMemo(
    () => invitations.filter((invitation) => invitation.status === 'pending'),
    [invitations]
  );

  if (!isOpen) {
    return null;
  }

  const handleSettingsPatch = async (patch: Partial<NoteCollaborationSettings>) => {
    setLocalSettings((current) => ({ ...current, ...patch }));
    setSavingSettings(true);

    try {
      await updateSettings(noteId, patch);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRoleUpdate = async (userId: string, role: 'editor' | 'reviewer' | 'viewer') => {
    setProcessingUserId(userId);
    try {
      await updateCollaboratorRole(noteId, userId, role);
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleRemoveCollaborator = async (userId: string) => {
    setProcessingUserId(userId);
    try {
      await removeCollaborator(noteId, userId);
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    setProcessingInvitationId(invitationId);
    try {
      await revokeInvitation(noteId, invitationId);
    } finally {
      setProcessingInvitationId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <div className="absolute inset-0 bg-black/20 pointer-events-auto" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl pointer-events-auto flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Collaboration Settings</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[240px]">
              {noteTitle || 'Selected note'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
            aria-label="Close collaboration settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 pt-3 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <button
            onClick={() => setActiveTab('collaborators')}
            className={`px-2.5 py-1 text-xs rounded ${activeTab === 'collaborators'
              ? 'bg-lex-accent-soft text-lex-accent'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            Collaborators
          </button>
          <button
            onClick={() => setActiveTab('invitations')}
            className={`px-2.5 py-1 text-xs rounded ${activeTab === 'invitations'
              ? 'bg-lex-accent-soft text-lex-accent'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            Invitations
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-2.5 py-1 text-xs rounded ${activeTab === 'activity'
              ? 'bg-lex-accent-soft text-lex-accent'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            Activity
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-2.5 py-1 text-xs rounded ${activeTab === 'settings'
              ? 'bg-lex-accent-soft text-lex-accent'
              : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            Settings
          </button>
        </div>

        {error && (
          <div className="mx-5 mt-3 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {activeTab === 'collaborators' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Team Access
                </h4>
                <button
                  onClick={onInviteClick}
                  className="px-2.5 py-1 text-xs rounded bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover"
                >
                  Invite
                </button>
              </div>

              {loadingCollaborators ? (
                <div className="py-8 flex justify-center">
                  <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : collaborators.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">No collaborators yet.</p>
              ) : (
                <div className="space-y-2">
                  {collaborators.map((collaborator) => (
                    <div
                      key={collaborator.id}
                      className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-700/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {collaborator.user_name || collaborator.user_email || collaborator.user_id}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {collaborator.user_email || collaborator.user_id}
                          </p>
                          {collaborator.last_active && (
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                              Active {formatRelativeDate(collaborator.last_active)}
                            </p>
                          )}
                        </div>

                        {collaborator.role === 'owner' || collaborator.is_owner ? (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                            Owner
                          </span>
                        ) : (
                          <select
                            value={collaborator.role}
                            onChange={(event) => handleRoleUpdate(
                              collaborator.user_id,
                              event.target.value as 'editor' | 'reviewer' | 'viewer'
                            )}
                            disabled={processingUserId === collaborator.user_id}
                            className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                          >
                            <option value="editor">Editor</option>
                            <option value="reviewer">Reviewer</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        )}
                      </div>

                      {collaborator.role !== 'owner' && !collaborator.is_owner && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => handleRemoveCollaborator(collaborator.user_id)}
                            disabled={processingUserId === collaborator.user_id}
                            className="text-[11px] text-red-600 dark:text-red-400 hover:text-red-700 disabled:opacity-50"
                          >
                            Remove access
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'invitations' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Pending ({activeInvitations.length})
                </h4>
                <button
                  onClick={onInviteClick}
                  className="px-2.5 py-1 text-xs rounded bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover"
                >
                  New Invite
                </button>
              </div>

              {loadingInvitations ? (
                <div className="py-8 flex justify-center">
                  <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : invitations.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">No invitations sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {invitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-700/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {invitation.invitee_name || invitation.invitee_email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {invitation.invitee_email}
                          </p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                            Role: {invitation.role} • Expires {formatRelativeDate(invitation.expires_at)}
                          </p>
                        </div>

                        <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                          invitation.status === 'pending'
                            ? 'bg-lex-accent-soft text-lex-accent'
                            : invitation.status === 'accepted'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {invitation.status}
                        </span>
                      </div>

                      {invitation.status === 'pending' && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => handleRevokeInvitation(invitation.id)}
                            disabled={processingInvitationId === invitation.id}
                            className="text-[11px] text-red-600 dark:text-red-400 hover:text-red-700 disabled:opacity-50"
                          >
                            Revoke invitation
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                Recent Activity
              </h4>

              {loadingActivity ? (
                <div className="py-8 flex justify-center">
                  <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : activity.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">No activity yet.</p>
              ) : (
                <div className="space-y-2">
                  {activity.map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-700/30"
                    >
                      <p className="text-sm text-gray-900 dark:text-white">
                        {humanizeActivityType(entry.activity_type)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {entry.user_name || entry.user_email || entry.user_id}
                      </p>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                        {formatRelativeDate(entry.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Collaboration Preferences
              </h4>

              {loadingSettings && !persistedSettings ? (
                <div className="py-8 flex justify-center">
                  <div className="w-5 h-5 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200">
                    <span>Comments enabled</span>
                    <input
                      type="checkbox"
                      checked={localSettings.comments_enabled}
                      onChange={(event) => handleSettingsPatch({ comments_enabled: event.target.checked })}
                    />
                  </label>

                  <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200">
                    <span>Track changes enabled</span>
                    <input
                      type="checkbox"
                      checked={localSettings.track_changes_enabled}
                      onChange={(event) => handleSettingsPatch({ track_changes_enabled: event.target.checked })}
                    />
                  </label>

                  <label className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-200">
                    <span>Suggestions mode</span>
                    <input
                      type="checkbox"
                      checked={localSettings.suggestions_mode}
                      onChange={(event) => handleSettingsPatch({ suggestions_mode: event.target.checked })}
                    />
                  </label>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Collaboration mode</label>
                    <select
                      value={localSettings.collaboration_mode}
                      onChange={(event) => handleSettingsPatch({
                        collaboration_mode: event.target.value as NoteCollaborationSettings['collaboration_mode'],
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="collaborative">Collaborative</option>
                      <option value="suggesting">Suggesting</option>
                      <option value="viewing">Viewing</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Auto-save (sec)</label>
                      <input
                        type="number"
                        min={3}
                        max={300}
                        value={localSettings.auto_save_interval}
                        onChange={(event) => handleSettingsPatch({ auto_save_interval: Number(event.target.value) })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Max editors</label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={localSettings.max_concurrent_editors}
                        onChange={(event) => handleSettingsPatch({ max_concurrent_editors: Number(event.target.value) })}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {savingSettings ? 'Saving settings...' : 'Changes are saved automatically.'}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default NoteCollaborationSettingsDrawer;
